import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { SidebarMenuButton } from "./sidebar";
import { ChevronsUpDown, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Shop, ShopLogo } from "./shop-logo";
import { PersonalAccount, PersonalAccountProps } from "./personal-account";
import ShopsList, { ShopsListProps } from "./shops-list";

export interface User {
  emails?: Array<{ address: string }>;
}

export interface ShopGroup {
  heading: string;
  shops: Shop[];
}

export interface AccountSwitcherProps {
  currentShop: Shop;
  user: User | null;
  shops?: Shop[];
  shopGroups?: ShopGroup[];
  onShopSelect: (shop: Shop) => void;
  /** When omitted, the settings entry is hidden (e.g. restricted roles). */
  onSettingsClick?: () => void;
  onLogout: () => void;
  noResultLabel?: string;
  settingsLabel?: string;
  logoutLabel?: string;
  shopsHeading?: string;
  settingsHeading?: string;
  /** When provided, shows a "Baback" internal section with a Super Admin entry */
  onAdminClick?: () => void;
  adminLabel?: string;
}

export default function AccountSwitcher({
  currentShop,
  user,
  shops,
  shopGroups,
  onShopSelect,
  onSettingsClick,
  onLogout,
  noResultLabel = "No results found",
  settingsLabel = "Account Preferences",
  logoutLabel = "Disconnect",
  shopsHeading = "Dashboard",
  settingsHeading = "Settings",
  onAdminClick,
  adminLabel = "Super Admin",
}: AccountSwitcherProps) {
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const handleShopSelect = (shop: Shop) => {
    onShopSelect(shop);
    setOpen(false);
  };

  const shopsListProps: ShopsListProps = {
    shops: shops ?? [],
    currentShop,
    onShopSelect: handleShopSelect,
    heading: shopsHeading,
  };

  const personalAccountProps: PersonalAccountProps = {
    onSettingsClick: onSettingsClick
      ? () => {
          onSettingsClick();
          setOpen(false);
        }
      : undefined,
    onLogout: () => {
      onLogout();
      setOpen(false);
    },
    settingsLabel,
    logoutLabel,
    heading: settingsHeading,
  };

  const name = user?.emails?.[0]?.address ?? "";

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <ShopLogo shop={currentShop} />
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{currentShop.name}</span>
            <span className="truncate text-xs">{name}</span>
          </div>
          <ChevronsUpDown className="ml-auto" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-72 rounded-lg p-0"
        side="bottom"
        align="start"
        sideOffset={4}
      >
        <Command
          filter={(value, search) => {
            const normalizeText = (text: string) =>
              text
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");

            const normalizedValue = normalizeText(value);
            const normalizedSearch = normalizeText(search);
            const match = normalizedValue.includes(normalizedSearch);
            return match ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder={currentShop.name}
            autoFocus
            className="border-0 focus:ring-0"
          />
          <div className="flex flex-col max-h-[400px]">
            <CommandList className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>{noResultLabel}</CommandEmpty>
              {shopGroups ? (
                shopGroups.map((group, index) => (
                  <ShopsList
                    key={index}
                    shops={group.shops}
                    currentShop={currentShop}
                    onShopSelect={handleShopSelect}
                    heading={group.heading}
                  />
                ))
              ) : (
                <ShopsList {...shopsListProps} />
              )}
            </CommandList>
            {onAdminClick && (
              <div className="border-t">
                <CommandGroup heading="Baback">
                  <CommandItem
                    onSelect={() => {
                      onAdminClick();
                      setOpen(false);
                    }}
                    className="cursor-pointer text-sm gap-2 flex font-medium text-muted-foreground items-center"
                  >
                    <ShieldCheck className="h-4 w-4 text-violet-500" />
                    <span className="text-violet-600 font-semibold">
                      {adminLabel}
                    </span>
                  </CommandItem>
                </CommandGroup>
              </div>
            )}
            <div className="border-t">
              <PersonalAccount {...personalAccountProps} />
            </div>
          </div>
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
